import { Link } from 'react-router-dom';
import BlurImage from './BlurImage';
import gardenPhoto from '../images/optimized/garden-tools-and-wheelbarrow-corner-web.jpg';
import joeWorkingPhoto from '../images/joeWorking.jpg';

const email = 'pleasantplaygroundgarden@gmail.com';
const locationLabel = '6757 Chew Ave, Philadelphia, PA 19119';
const mapQuery = '40.057611,-75.181389';

const contactReasons = [
  {
    title: 'Join the garden',
    text: 'Interested in a bed, the waitlist, or learning how membership works?',
    to: '/apply',
    action: 'Apply for Waitlist',
  },
  {
    title: 'Volunteer',
    text: 'Ask about garden days, community events, or lending a hand when help is needed.',
    href: `mailto:${email}?subject=Volunteering%20at%20Pleasant%20Playground%20Community%20Garden`,
    action: 'Email About Volunteering',
  },
  {
    title: 'Visit us',
    text: 'Find the garden at Pleasant Playground and open directions before you head over.',
    href: `https://www.google.com/maps?q=${mapQuery}`,
    action: 'Open Directions',
    external: true,
  },
];

function ContactCard({ reason }) {
  const className =
    'group flex h-full min-w-0 flex-col justify-between border border-[#d5cfc0] bg-[#fffdf7] p-5 transition-shadow hover:shadow-md sm:p-6';
  const content = (
    <>
      <div>
        <h3 className="font-serif text-2xl leading-tight text-[#10351f]">{reason.title}</h3>
        <p className="mt-3 text-sm leading-6 text-[#5b695c]">{reason.text}</p>
      </div>
      <span className="mt-6 inline-flex min-w-0 items-center justify-between gap-4 border-t border-[#ded6c6] pt-4 text-sm font-semibold text-[#8e3a2b]">
        <span className="min-w-0">{reason.action}</span>
        <span className="shrink-0 text-xl leading-none transition-transform group-hover:translate-x-1" aria-hidden="true">
          &rarr;
        </span>
      </span>
    </>
  );

  if (reason.to) {
    return (
      <Link to={reason.to} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <a
      href={reason.href}
      target={reason.external ? '_blank' : undefined}
      rel={reason.external ? 'noreferrer' : undefined}
      className={className}
    >
      {content}
    </a>
  );
}

function ContactForm() {
  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto grid max-w-[92rem] gap-10 px-6 py-12 sm:px-10 md:py-16 lg:grid-cols-[minmax(0,0.92fr)_minmax(22rem,0.72fr)] lg:items-end lg:px-16">
          <div className="max-w-3xl">
            <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl md:text-7xl">
              Get In Touch
            </h1>
            <div className="mt-5 h-px w-24 bg-[#304536]" />
            <p className="font-handwritten mt-5 max-w-2xl text-xl leading-snug text-[#243c2c] sm:text-2xl">
              Questions, volunteer interest, membership, or garden updates: start here.
            </p>
            <p className="mt-7 max-w-2xl text-base leading-7 text-[#445044] sm:text-lg sm:leading-8">
              Pleasant Playground Community Garden is rooted at 6757 Chew Avenue. Reach out by email, join the waitlist,
              or get directions before visiting the garden.
            </p>
          </div>

          <div className="overflow-hidden border border-[#d3cbbb] bg-[#f8f4ea]">
            <BlurImage
              src={joeWorkingPhoto}
              alt="Joe working in the Pleasant Playground Community Garden"
              className="aspect-[16/11] w-full object-cover lg:aspect-[5/4]"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]">
        <div className="mx-auto max-w-[92rem] px-6 py-12 sm:px-10 md:py-16 lg:px-16">
          <div className="mb-8 flex flex-col gap-5 border-b border-[#cfc7b6] pb-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Contact paths
              </p>
              <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
                What can we help with?
              </h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-[#5b695c]">
              Choose the path that fits your question best. Everything still routes back to the garden team.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {contactReasons.map((reason) => (
              <ContactCard key={reason.title} reason={reason} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f1eadc]">
        <div className="mx-auto grid max-w-[92rem] gap-10 px-6 py-12 sm:px-10 md:py-16 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1fr)] lg:px-16">
          <div className="min-w-0">
            <div className="border border-[#d5cfc0] bg-[#fffdf7] p-6 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Email
              </p>
              <h2 className="mt-2 font-serif text-4xl leading-tight text-[#10351f]">
                Send us a note
              </h2>
              <p className="mt-5 text-base leading-7 text-[#445044]">
                For membership interest, volunteering, produce box questions, event details, or general garden updates,
                email us directly.
              </p>
              <a
                href={`mailto:${email}`}
                className="mt-7 inline-flex w-full min-w-0 items-center justify-between gap-3 border border-[#43513f] bg-[#10351f] px-5 py-4 text-base font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7]"
              >
                <span className="min-w-0 break-all text-left">{email}</span>
                <span className="shrink-0" aria-hidden="true">&rarr;</span>
              </a>
            </div>

            <div className="mt-6 overflow-hidden border border-[#d3cbbb]">
              <BlurImage
                src={gardenPhoto}
                alt="Garden tools and wheelbarrow at Pleasant Playground Community Garden"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="mb-5 flex flex-col gap-3 border-b border-[#c2b8a5] pb-5 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                  Find us
                </p>
                <h2 className="mt-2 font-serif text-4xl leading-tight text-[#10351f]">
                  Pleasant Playground
                </h2>
              </div>
              <a
                href={`https://www.google.com/maps?q=${mapQuery}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-full min-w-0 items-center justify-between gap-3 border border-[#43513f] px-4 py-3 text-sm font-medium text-[#18271e] transition-colors hover:bg-[#10351f] hover:text-[#fbf8ef] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] sm:w-auto sm:min-w-44"
              >
                <span>Directions</span>
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>

            <p className="mb-5 text-base leading-7 text-[#445044]">
              <a
                href={`https://www.google.com/maps?q=${mapQuery}`}
                target="_blank"
                rel="noreferrer"
                className="break-words font-semibold text-[#10351f] underline decoration-[#cfc7b6] underline-offset-4 hover:text-[#8e3a2b]"
              >
                {locationLabel}
              </a>
            </p>

            <div className="overflow-hidden border border-[#d3cbbb] bg-[#fffdf7]">
              <iframe
                title="Pleasant Playground Community Garden Map"
                src={`https://www.google.com/maps?q=${mapQuery}&t=k&z=18&output=embed`}
                width="100%"
                height="430"
                style={{ border: 0, display: 'block' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactForm;
